import _ from "lodash";
import React from "react";
import classes from "./MoreFiltersBar.scss";
import { useI18n } from "Hooks";
import PropTypes from "prop-types";
import classNames from "classnames";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  isMYPWorksheetEnabledForLibrary,
  getIsMYPGrade,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getOrganizationCurriculumProgramsQuery } from "modules/CommonQuery";
import { CURRICULUM_TYPE_MYP } from "Constants/stringConstants";
import WorksheetAdditionalFilter from "../../LeFeed/WorksheetAdditionalFilter";

/**
 * This Component is used to display the selected more filters in the bar.
 * Example can be seen in classroom - Import Learning Experience from LELibrary Modal
 *
 *
 * @param selectedMoreFilters -  An array of Selected more filters
 * 
 * Ex : [
      {
        id: FILTER_NAME,
        name: FILTER_NAME,
        options: filterOptions,
        label: "common:label",
        showSearchBar: false
      },
      {
        id: FILTER_NAME,
        name: FILTER_NAME,
        options: filterOptions,
        label: "common:label",
        showSearchBar: false
      }

 * @param getFilterDropdown : Function which takes filter, filter value, update filterValue as 
 * input and returns FilterDropDown
 * 
 * Ex : const getFilterDropdown = ({filter,onChangeFilterValues,filterValues}) => {
                return (
                        <FilterDropdown
                        key={filter.id}
                        name={filter.name}
                        options={filter.options}
                        label={filter.label}
                        showSearchBar={filter.showSearchBar}
                        isCheckList={filter.isCheckList}
                        showAllOption={filter.showAllOption}
                        updateInputField={onChangeFilterValues}
                        value={_.get(filterValues, `${filter.name}`, [])}
                        isSearchBasedOnQuery={filter.isSearchBasedOnQuery}
                        organizationId={filter.organizationId}
                        graphqlQueryEnhancer={filter.graphqlQueryEnhancer}
                        filterBoxStyle={styles.filterBoxStyle}
                        buttonDropDownContainerStyle={styles.buttonDropDownContainerStyle}
                        arrowContainerStyle={styles.arrowContainerStyle}
                        nameTextStyle={styles.nameTextStyle}
                        filterTextStyle={styles.filterTextStyle}
                        emptyTextStyle={styles.nameTextStyle}
                        />
                      );
                    };
                    
 * @param filterValues - An object with filter names as keys and selected filter values as values
 *
 * Ex : [
 *      {filterOne : [1, 2, 3]
 *      filterTwo : ["ALL"]}
 *      ]
 * 
 * @param onChangeFilterValues : Function to update filter Values
 * @param removeFilter : Function used to remove filter on clicking close icon
 *
 *
 */

const styles = {
  dropdownStyle: {
    maxWidth: "400px",
  },
};

const MoreFiltersBar = props => {
  const {
    testId,
    selectedMoreFilters,
    getFilterDropdown,
    filterValues,
    onChangeFilterValues,
    groupType,
    importFromToddleLibrary,
    style,
    selectedGradeData,
    selectedSubjectData,
    isWorksheetAssessment,
    curriculumProgramId,
    isMypGrade,
  } = props;

  const { t } = useI18n(["common", "snp"]);

  const getMoreFiltersContainerClasses = () => {
    if (importFromToddleLibrary) {
      return classes.moreFiltersContainer;
    } else {
      return classNames(
        classes.moreFiltersContainer,
        classes.containerBackground,
        classes.containerMargin
      );
    }
  };

  return (
    <div className={getMoreFiltersContainerClasses()} style={style}>
      {_.map(selectedMoreFilters, filter => {
        return (
          <div className={classes.filter} key={filter.id}>
            {getFilterDropdown({
              testId,
              searchPlaceholder: t("common:search"),
              filter,
              filterValues,
              onChangeFilterValues,
              t,
              groupType,
              styles,
            })}
          </div>
        );
      })}
      {isWorksheetAssessment && isMypGrade ? (
        <div className={classes.filter}>
          <WorksheetAdditionalFilter
            onChangeFilterValues={onChangeFilterValues}
            selectedGradeData={selectedGradeData}
            selectedSubjectData={selectedSubjectData}
            curriculumProgramId={curriculumProgramId}
          />
        </div>
      ) : null}
    </div>
  );
};

MoreFiltersBar.propTypes = {
  selectedMoreFilters: PropTypes.array,
  filterValues: PropTypes.object,
  onChangeFilterValues: PropTypes.func,
  getFilterDropdown: PropTypes.func,
  removeFilter: PropTypes.func,
  testId: PropTypes.string,
  selectedGradeData: PropTypes.object,
  selectedSubjectData: PropTypes.object,
  isWorksheetAssessment: PropTypes.bool,
  curriculumProgramId: PropTypes.string,
};

MoreFiltersBar.defaultProps = {
  testId: "",
  selectedMoreFilters: [],
  filterValues: {},
  isWorksheetAssessment: false,
  importFromToddleLibrary: false,
};

const mapStateToProps = (state, ownProps) => {
  const { selectedGradeData, importFromToddleLibrary } = ownProps;

  let organizationId, shouldFetchCurriculumData;
  const isMypGrade = getIsMYPGrade({
    globalGradeUid: _.get(selectedGradeData, "label", ""),
  });
  if (isMypGrade) {
    const isMYPWorksheetEnabled = isMYPWorksheetEnabledForLibrary();

    shouldFetchCurriculumData =
      isMYPWorksheetEnabled && importFromToddleLibrary;

    ({ org_id: organizationId } = getUserInfo({ portalType: "COMMUNITY" }));
  }

  return {
    shouldFetchCurriculumData,
    organizationId,
    isMypGrade,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getOrganizationCurriculumProgramsQuery, {
    name: "getOrganizationCurriculumPrograms",
    skip: ({ shouldFetchCurriculumData, organizationId }) =>
      !shouldFetchCurriculumData || !organizationId,
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        portalType: "COMMUNITY",
      },
    }),
    props({ getOrganizationCurriculumPrograms }) {
      const curriculumProgramData = _.get(
        getOrganizationCurriculumPrograms,
        "node.curriculumPrograms"
      );
      const mypProgram = _.find(curriculumProgramData, {
        type: CURRICULUM_TYPE_MYP,
      });
      const curriculumProgramId = _.get(mypProgram, "id", "");
      return {
        curriculumProgramId,
      };
    },
  })
)(MoreFiltersBar);
